import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { VenuePicker } from "@/components/VenuePicker";
import { portalSurface } from "@/components/producer";

const NEW = "__new__";

/**
 * Lets a producer pick one of their saved (reusable, org-scoped) locations or
 * enter a brand-new one. Emits { location, location_id, location_details }.
 * - location_id set  → reuse an existing saved location
 * - location_id null → a new location (created server-side on submit)
 */
export function LocationField({ value, onChange, theme = "light" }) {
  const { data } = useQuery({
    queryKey: ["producer-locations"],
    queryFn: async () => (await api.get("/producer/locations")).data,
  });
  const saved = data?.locations || [];

  // Surface the event's current location even if it predates saved-locations.
  const options = useMemo(() => {
    const list = [...saved];
    if (value?.location_id && !saved.some((l) => l.location_id === value.location_id)) {
      list.unshift({
        location_id: value.location_id,
        label: value.location_details?.name || value.location || "Current location",
        location: value.location,
        location_details: value.location_details || null,
        city: value.location_details?.city,
      });
    }
    return list;
  }, [saved, value?.location_id, value?.location]);

  const isNew = value?.location_id == null && (value?.location || value?._mode === NEW);
  const selectValue = value?.location_id || (isNew ? NEW : "");

  const onSelect = (v) => {
    if (v === NEW) {
      onChange({ location: "", location_id: null, location_details: null, _mode: NEW });
      return;
    }
    const loc = options.find((l) => l.location_id === v);
    if (loc) onChange({ location: loc.location, location_id: loc.location_id, location_details: loc.location_details || null });
  };

  // No saved locations yet → go straight to entering a new one.
  const noSaved = options.length === 0;

  return (
    <div data-testid="location-field">
      {!noSaved && (
        <>
          <Label className="mb-1.5 block text-xs p-muted">Choose a saved location or add a new one</Label>
          <Select value={selectValue || undefined} onValueChange={onSelect}>
            <SelectTrigger data-testid="location-select-trigger">
              <SelectValue placeholder="Select a location…" />
            </SelectTrigger>
            <SelectContent className={portalSurface(theme)}>
              {options.map((l) => (
                <SelectItem key={l.location_id} value={l.location_id} data-testid={`location-option-${l.location_id}`}>
                  <span className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 opacity-70" />
                    {l.label || l.location}{l.city ? ` · ${l.city}` : ""}
                  </span>
                </SelectItem>
              ))}
              <SelectItem value={NEW} data-testid="location-option-new">
                <span className="flex items-center gap-2"><Plus className="h-3.5 w-3.5" /> Enter a new location</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </>
      )}

      {(noSaved || selectValue === NEW) && (
        <div className={noSaved ? "" : "mt-3"}>
          <VenuePicker
            autoFocus={!noSaved}
            onChange={(location, details) =>
              onChange({ location, location_id: null, location_details: details, _mode: NEW })}
          />
        </div>
      )}
    </div>
  );
}
